import React, { useState } from 'react';
import { TruckIcon, UserIcon, WrenchIcon, ClipboardListIcon, PlusIcon, AlertTriangleIcon, CalendarIcon, FileTextIcon, MapPinIcon, CalendarDaysIcon, Settings2Icon, BuildingIcon, CheckCircleIcon, ArrowRightIcon } from 'lucide-react';
import { ActionCard } from '../../dashboard/ActionCard';
import { TabNavigation } from '../../dashboard/TabNavigation';
import { FleetManagement } from './FleetManagement';
import { DriverManagement } from './DriverManagement';
import { MaintenanceSchedule } from './MaintenanceSchedule';
import { JobAssignments } from './JobAssignments';
import { TruckAvailability } from './TruckAvailability';
import { TruckingCompanyProfile } from './TruckingCompanyProfile';
import { MessageNotifications } from '../../messaging/MessageNotifications';
import { JobMessageButton } from '../../messaging/JobMessageButton';
interface Truck {
  id: string;
  truckNumber: string;
  type: string;
  make: string;
  model: string;
  year: number;
  vin: string;
  plateNumber: string;
  status: 'available' | 'assigned' | 'maintenance' | 'out-of-service';
  lastMaintenance: string;
  nextMaintenance: string;
  mileage: number;
  currentHours: number;
  hoursSinceLastMaintenance: number;
  maintenanceIntervalHours: number;
  assignedDriver?: string;
  currentJob?: string;
  availability?: {
    [date: string]: boolean;
  };
}
interface Driver {
  id: string;
  name: string;
  email: string;
  phone: string;
  cdlNumber: string;
  cdlClass: string;
  cdlExpiration: string;
  medicalCardExpiration: string;
  hireDate: string;
  status: 'active' | 'inactive' | 'on-leave';
  emergencyContact: {
    name: string;
    phone: string;
    relationship: string;
  };
}
interface Job {
  id: string;
  jobName: string;
  jobNumber: string;
  date: string;
  startTime: string;
  material: string;
  truckType: string;
  loadLocation: string;
  dumpLocation: string;
  foreman: string;
  foremanPhone: string;
  estimatedHours: number;
  hourlyRate: number;
  source: 'broker' | 'contractor';
  sourceName: string;
  priority: 'low' | 'medium' | 'high';
  status: 'pending' | 'assigned' | 'in-progress' | 'completed';
  assignedTruck?: string;
  assignedDriver?: string;
  comments?: string;
  geoPunchRequired?: boolean;
  geoPunchDistance?: number;
}
interface InProgressJob {
  id: string;
  jobName: string;
  jobNumber: string;
  date: string;
  startTime: string;
  material: string;
  truckType: string;
  loadLocation: string;
  dumpLocation: string;
  source: 'broker' | 'contractor';
  sourceName: string;
  assignedTruck: string;
  assignedDriver: string;
  progress: number;
  loadCount: number;
  totalLoads: number;
  tonnage?: {
    current: number;
    total: number;
  };
  status: 'in-progress' | 'paused' | 'delayed';
  estimatedCompletion?: string;
}
interface BusinessPartner {
  id: string;
  name: string;
  company: string;
  role: 'broker' | 'contractor';
  email: string;
  phone: string;
  status: 'active' | 'pending' | 'inactive';
  connectedSince: string;
}
interface TruckingCompanyDashboardProps {
  onNavigate?: (page: string) => void;
}
export const TruckingCompanyDashboard = ({
  onNavigate
}: TruckingCompanyDashboardProps) => {
  const [activeTab, setActiveTab] = useState('overview');
  // Sample fleet data with availability information - Updated to 18 trucks
  const [trucks, setTrucks] = useState<Truck[]>([{
    id: '1',
    truckNumber: 'T-001',
    type: 'dump truck 12',
    make: 'Peterbilt',
    model: '567',
    year: 2020,
    vin: '1XPWD40X1ED215307',
    plateNumber: 'TRK-001',
    status: 'available',
    lastMaintenance: '2024-01-01',
    nextMaintenance: '2024-04-01',
    mileage: 125000,
    currentHours: 4250,
    hoursSinceLastMaintenance: 420,
    maintenanceIntervalHours: 400,
    assignedDriver: '1',
    availability: {
      [getDateString(1)]: true,
      [getDateString(2)]: true,
      [getDateString(3)]: false,
      [getDateString(4)]: true,
      [getDateString(5)]: true,
      [getDateString(6)]: false,
      [getDateString(7)]: false
    }
  }, {
    id: '2',
    truckNumber: 'T-002',
    type: 'strong arm 14',
    make: 'Kenworth',
    model: 'T880',
    year: 2019,
    vin: '1XKWD40X2KJ215308',
    plateNumber: 'TRK-002',
    status: 'assigned',
    lastMaintenance: '2023-12-15',
    nextMaintenance: '2024-03-15',
    mileage: 142000,
    currentHours: 3980,
    hoursSinceLastMaintenance: 450,
    maintenanceIntervalHours: 400,
    assignedDriver: '2',
    currentJob: 'job-1',
    availability: {
      [getDateString(1)]: false,
      [getDateString(2)]: true,
      [getDateString(3)]: true,
      [getDateString(4)]: true,
      [getDateString(5)]: true,
      [getDateString(6)]: true,
      [getDateString(7)]: true
    }
  }, {
    id: '3',
    truckNumber: 'T-003',
    type: 'side dump',
    make: 'Mack',
    model: 'Granite',
    year: 2021,
    vin: '1M2AN07Y1MW123456',
    plateNumber: 'TRK-003',
    status: 'maintenance',
    lastMaintenance: '2024-01-10',
    nextMaintenance: '2025-09-01',
    mileage: 87000,
    currentHours: 2900,
    hoursSinceLastMaintenance: 180,
    maintenanceIntervalHours: 400,
    assignedDriver: '3',
    availability: {
      [getDateString(1)]: false,
      [getDateString(2)]: false,
      [getDateString(3)]: true,
      [getDateString(4)]: true,
      [getDateString(5)]: true,
      [getDateString(6)]: true,
      [getDateString(7)]: true
    }
  }, {
    id: '4',
    truckNumber: 'T-004',
    type: 'dump truck 10',
    make: 'Freightliner',
    model: '114SD',
    year: 2018,
    vin: '1FVAG5CY8JHDG4567',
    plateNumber: 'TRK-004',
    status: 'available',
    lastMaintenance: '2023-11-20',
    nextMaintenance: '2026-02-20',
    mileage: 156000,
    currentHours: 5200,
    hoursSinceLastMaintenance: 380,
    maintenanceIntervalHours: 400,
    assignedDriver: '4',
    availability: {
      [getDateString(1)]: true,
      [getDateString(2)]: true,
      [getDateString(3)]: true,
      [getDateString(4)]: false,
      [getDateString(5)]: true,
      [getDateString(6)]: true,
      [getDateString(7)]: true
    }
  }, {
    id: '5',
    truckNumber: 'T-005',
    type: 'dump truck 14+',
    make: 'Peterbilt',
    model: '348',
    year: 2022,
    vin: '1NPALU0X8ND123890',
    plateNumber: 'TRK-005',
    status: 'available',
    lastMaintenance: '2024-01-05',
    nextMaintenance: '2026-04-05',
    mileage: 45000,
    currentHours: 1500,
    hoursSinceLastMaintenance: 390,
    maintenanceIntervalHours: 400,
    assignedDriver: '5',
    availability: {
      [getDateString(1)]: true,
      [getDateString(2)]: false,
      [getDateString(3)]: true,
      [getDateString(4)]: true,
      [getDateString(5)]: true,
      [getDateString(6)]: true,
      [getDateString(7)]: false
    }
  }, {
    id: '6',
    truckNumber: 'T-006',
    type: 'strong arm 12',
    make: 'International',
    model: 'HX620',
    year: 2020,
    vin: '3HAMMAAR8KL567234',
    plateNumber: 'TRK-006',
    status: 'assigned',
    lastMaintenance: '2023-12-01',
    nextMaintenance: '2026-03-01',
    mileage: 112000,
    currentHours: 3740,
    hoursSinceLastMaintenance: 385,
    maintenanceIntervalHours: 400,
    assignedDriver: '6',
    currentJob: 'job-2',
    availability: {
      [getDateString(1)]: false,
      [getDateString(2)]: true,
      [getDateString(3)]: true,
      [getDateString(4)]: true,
      [getDateString(5)]: true,
      [getDateString(6)]: true,
      [getDateString(7)]: true
    }
  }, {
    id: '7',
    truckNumber: 'T-007',
    type: 'strong arm 16+',
    make: 'Volvo',
    model: 'VHD64F200',
    year: 2019,
    vin: '4V4NC9EH8KN789456',
    plateNumber: 'TRK-007',
    status: 'available',
    lastMaintenance: '2023-10-15',
    nextMaintenance: '2026-01-15',
    mileage: 134000,
    currentHours: 4470,
    hoursSinceLastMaintenance: 350,
    maintenanceIntervalHours: 400,
    assignedDriver: '7',
    availability: {
      [getDateString(1)]: true,
      [getDateString(2)]: true,
      [getDateString(3)]: true,
      [getDateString(4)]: true,
      [getDateString(5)]: true,
      [getDateString(6)]: false,
      [getDateString(7)]: true
    }
  }, {
    id: '8',
    truckNumber: 'T-008',
    type: 'super side 30tn+',
    make: 'Kenworth',
    model: 'T470',
    year: 2021,
    vin: '1XKDD40X4MJ345678',
    plateNumber: 'TRK-008',
    status: 'available',
    lastMaintenance: '2024-01-12',
    nextMaintenance: '2026-04-12',
    mileage: 78000,
    currentHours: 2600,
    hoursSinceLastMaintenance: 320,
    maintenanceIntervalHours: 400,
    assignedDriver: '8',
    availability: {
      [getDateString(1)]: true,
      [getDateString(2)]: true,
      [getDateString(3)]: false,
      [getDateString(4)]: true,
      [getDateString(5)]: true,
      [getDateString(6)]: true,
      [getDateString(7)]: true
    }
  }, {
    id: '9',
    truckNumber: 'T-009',
    type: 'jumbo side 35tn+',
    make: 'Mack',
    model: 'TerraPro',
    year: 2020,
    vin: '1M2AX07C5LM456789',
    plateNumber: 'TRK-009',
    status: 'maintenance',
    lastMaintenance: '2024-01-08',
    nextMaintenance: '2026-01-25',
    mileage: 95000,
    currentHours: 3170,
    hoursSinceLastMaintenance: 220,
    maintenanceIntervalHours: 400,
    assignedDriver: '9',
    availability: {
      [getDateString(1)]: false,
      [getDateString(2)]: false,
      [getDateString(3)]: false,
      [getDateString(4)]: true,
      [getDateString(5)]: true,
      [getDateString(6)]: true,
      [getDateString(7)]: true
    }
  }, {
    id: '10',
    truckNumber: 'T-010',
    type: 'belly dump',
    make: 'Peterbilt',
    model: '367',
    year: 2018,
    vin: '1XPBDP9X5JD567890',
    plateNumber: 'TRK-010',
    status: 'available',
    lastMaintenance: '2023-11-30',
    nextMaintenance: '2026-02-28',
    mileage: 142000,
    currentHours: 4730,
    hoursSinceLastMaintenance: 395,
    maintenanceIntervalHours: 400,
    assignedDriver: '10',
    availability: {
      [getDateString(1)]: true,
      [getDateString(2)]: true,
      [getDateString(3)]: true,
      [getDateString(4)]: true,
      [getDateString(5)]: false,
      [getDateString(6)]: true,
      [getDateString(7)]: false
    }
  }, {
    id: '11',
    truckNumber: 'T-011',
    type: 'double belly',
    make: 'International',
    model: 'HX Series',
    year: 2019,
    vin: '3HAMMAAR2KL678901',
    plateNumber: 'TRK-011',
    status: 'assigned',
    lastMaintenance: '2023-12-20',
    nextMaintenance: '2026-03-20',
    mileage: 118000,
    currentHours: 3940,
    hoursSinceLastMaintenance: 260,
    maintenanceIntervalHours: 400,
    assignedDriver: '11',
    currentJob: 'job-3',
    availability: {
      [getDateString(1)]: false,
      [getDateString(2)]: false,
      [getDateString(3)]: true,
      [getDateString(4)]: true,
      [getDateString(5)]: true,
      [getDateString(6)]: true,
      [getDateString(7)]: true
    }
  }, {
    id: '12',
    truckNumber: 'T-012',
    type: 'dump truck 12',
    make: 'Freightliner',
    model: 'M2 106',
    year: 2021,
    vin: '1FVACWDT9MHAB2345',
    plateNumber: 'TRK-012',
    status: 'available',
    lastMaintenance: '2024-01-03',
    nextMaintenance: '2026-04-03',
    mileage: 62000,
    currentHours: 2070,
    hoursSinceLastMaintenance: 280,
    maintenanceIntervalHours: 400,
    assignedDriver: '12',
    availability: {
      [getDateString(1)]: true,
      [getDateString(2)]: true,
      [getDateString(3)]: true,
      [getDateString(4)]: false,
      [getDateString(5)]: true,
      [getDateString(6)]: false,
      [getDateString(7)]: true
    }
  }, {
    id: '13',
    truckNumber: 'T-013',
    type: 'side dump',
    make: 'Volvo',
    model: 'VHD84F200',
    year: 2022,
    vin: '4V4NC9EJ2NN345678',
    plateNumber: 'TRK-013',
    status: 'available',
    lastMaintenance: '2024-01-15',
    nextMaintenance: '2026-04-15',
    mileage: 38000,
    currentHours: 1270,
    hoursSinceLastMaintenance: 200,
    maintenanceIntervalHours: 400,
    assignedDriver: '13',
    availability: {
      [getDateString(1)]: true,
      [getDateString(2)]: false,
      [getDateString(3)]: true,
      [getDateString(4)]: true,
      [getDateString(5)]: true,
      [getDateString(6)]: true,
      [getDateString(7)]: true
    }
  }, {
    id: '14',
    truckNumber: 'T-014',
    type: 'dump truck 10',
    make: 'Kenworth',
    model: 'T370',
    year: 2017,
    vin: '1XKDD40X0HJ456789',
    plateNumber: 'TRk-014',
    status: 'out-of-service',
    lastMaintenance: '2024-01-02',
    nextMaintenance: '2026-02-15',
    mileage: 178000,
    currentHours: 5930,
    hoursSinceLastMaintenance: 310,
    maintenanceIntervalHours: 400,
    assignedDriver: '14',
    availability: {
      [getDateString(1)]: false,
      [getDateString(2)]: false,
      [getDateString(3)]: false,
      [getDateString(4)]: false,
      [getDateString(5)]: true,
      [getDateString(6)]: true,
      [getDateString(7)]: true
    }
  }, {
    id: '15',
    truckNumber: 'T-015',
    type: 'strong arm 14',
    make: 'Mack',
    model: 'Anthem',
    year: 2020,
    vin: '1M1AX07Y5LM567890',
    plateNumber: 'TRK-015',
    status: 'available',
    lastMaintenance: '2023-12-10',
    nextMaintenance: '2026-03-10',
    mileage: 89000,
    currentHours: 2970,
    hoursSinceLastMaintenance: 240,
    maintenanceIntervalHours: 400,
    assignedDriver: '15',
    availability: {
      [getDateString(1)]: true,
      [getDateString(2)]: true,
      [getDateString(3)]: true,
      [getDateString(4)]: true,
      [getDateString(5)]: true,
      [getDateString(6)]: true,
      [getDateString(7)]: false
    }
  }, {
    id: '16',
    truckNumber: 'T-016',
    type: 'dump truck 14+',
    make: 'Peterbilt',
    model: '579',
    year: 2023,
    vin: '1XP5DB9X3PD678901',
    plateNumber: 'TRK-016',
    status: 'available',
    lastMaintenance: '2024-01-18',
    nextMaintenance: '2026-04-18',
    mileage: 12000,
    currentHours: 400,
    hoursSinceLastMaintenance: 120,
    maintenanceIntervalHours: 400,
    assignedDriver: '16',
    availability: {
      [getDateString(1)]: true,
      [getDateString(2)]: true,
      [getDateString(3)]: false,
      [getDateString(4)]: true,
      [getDateString(5)]: false,
      [getDateString(6)]: true,
      [getDateString(7)]: true
    }
  }, {
    id: '17',
    truckNumber: 'T-017',
    type: 'super side 30tn+',
    make: 'International',
    model: 'HX520',
    year: 2019,
    vin: '3HAMMAAR3KL789012',
    plateNumber: 'TRK-017',
    status: 'assigned',
    lastMaintenance: '2023-11-25',
    nextMaintenance: '2026-02-25',
    mileage: 125000,
    currentHours: 4170,
    hoursSinceLastMaintenance: 375,
    maintenanceIntervalHours: 400,
    assignedDriver: '17',
    currentJob: 'job-4',
    availability: {
      [getDateString(1)]: false,
      [getDateString(2)]: true,
      [getDateString(3)]: true,
      [getDateString(4)]: false,
      [getDateString(5)]: true,
      [getDateString(6)]: true,
      [getDateString(7)]: true
    }
  }, {
    id: '18',
    truckNumber: 'T-018',
    type: 'belly dump',
    make: 'Freightliner',
    model: 'Cascadia',
    year: 2021,
    vin: '1FUJGBDV5MLBC3456',
    plateNumber: 'TRK-018',
    status: 'available',
    lastMaintenance: '2024-01-07',
    nextMaintenance: '2026-04-07',
    mileage: 71000,
    currentHours: 2370,
    hoursSinceLastMaintenance: 370,
    maintenanceIntervalHours: 400,
    assignedDriver: '18',
    availability: {
      [getDateString(1)]: true,
      [getDateString(2)]: true,
      [getDateString(3)]: true,
      [getDateString(4)]: true,
      [getDateString(5)]: true,
      [getDateString(6)]: false,
      [getDateString(7)]: true
    }
  }]);
  // Sample business partners data (brokers and contractors)
  const [businessPartners, setBusinessPartners] = useState<BusinessPartner[]>([{
    id: 'broker1',
    name: 'John Smith',
    company: 'Alpine Hauling',
    role: 'broker',
    email: 'john@alpinehauling.com',
    phone: '555-123-4567',
    status: 'active',
    connectedSince: '2023-05-15'
  }, {
    id: 'dirt-logics-broker',
    name: 'Dirt Logics Broker',
    company: 'Dirt Logics Brokerage',
    role: 'broker',
    email: 'broker@dirtlogics.com',
    phone: '555-321-7654',
    status: 'active',
    connectedSince: '2023-01-01'
  }, {
    id: 'dirt-logics-contractor',
    name: 'Dirt Logics Contractor',
    company: 'Dirt Logics Construction',
    role: 'contractor',
    email: 'contractor@dirtlogics.com',
    phone: '555-789-0123',
    status: 'active',
    connectedSince: '2023-01-01'
  }]);
  // Sample in-progress jobs data
  const [inProgressJobs] = useState<InProgressJob[]>([{
    id: 'in-progress-1',
    jobName: 'Downtown Commercial Complex Phase 1',
    jobNumber: 'DCC-2024-001',
    date: '2024-01-16',
    startTime: '07:00',
    material: 'Gravel',
    truckType: 'dump truck 12',
    loadLocation: 'West Valley Materials Yard',
    dumpLocation: 'Downtown SLC Project',
    source: 'broker',
    sourceName: 'Alpine Hauling',
    assignedTruck: 'T-002',
    assignedDriver: 'Mike Johnson',
    progress: 65,
    loadCount: 5,
    totalLoads: 8,
    tonnage: {
      current: 150,
      total: 240
    },
    status: 'in-progress',
    estimatedCompletion: '16:30'
  }, {
    id: 'in-progress-2',
    jobName: 'Highway 89 Expansion Project',
    jobNumber: 'HWY-2024-002',
    date: '2024-01-16',
    startTime: '08:00',
    material: 'Sand',
    truckType: 'strong arm 14',
    loadLocation: 'Sand Pit North',
    dumpLocation: 'Highway 89 Site - Mile Marker 15',
    source: 'contractor',
    sourceName: 'ABC Construction',
    assignedTruck: 'T-006',
    assignedDriver: 'Tom Rodriguez',
    progress: 40,
    loadCount: 3,
    totalLoads: 7,
    tonnage: {
      current: 90,
      total: 210
    },
    status: 'in-progress',
    estimatedCompletion: '15:00'
  }, {
    id: 'in-progress-3',
    jobName: 'Residential Development Phase 2',
    jobNumber: 'RES-2024-003',
    date: '2024-01-16',
    startTime: '07:30',
    material: 'Road Base',
    truckType: 'double belly',
    loadLocation: 'Gravel Yard East',
    dumpLocation: 'Sugar House Development',
    source: 'broker',
    sourceName: 'Dirt Logics Brokerage',
    assignedTruck: 'T-011',
    assignedDriver: 'David Chen',
    progress: 80,
    loadCount: 8,
    totalLoads: 10,
    tonnage: {
      current: 240,
      total: 300
    },
    status: 'in-progress',
    estimatedCompletion: '14:30'
  }, {
    id: 'in-progress-4',
    jobName: 'East Valley Commercial Plaza',
    jobNumber: 'EVC-2024-005',
    date: '2024-01-16',
    startTime: '08:30',
    material: 'Topsoil',
    truckType: 'super side 30tn+',
    loadLocation: 'Topsoil Depot',
    dumpLocation: 'East Valley Plaza Site',
    source: 'contractor',
    sourceName: 'Mountain Development LLC',
    assignedTruck: 'T-017',
    assignedDriver: 'Robert Williams',
    progress: 25,
    loadCount: 2,
    totalLoads: 8,
    tonnage: {
      current: 60,
      total: 240
    },
    status: 'delayed',
    estimatedCompletion: '17:00'
  }]);
  // Helper function to get date strings for the next N days
  function getDateString(daysFromNow: number): string {
    const date = new Date();
    date.setDate(date.getDate() + daysFromNow);
    return date.toISOString().split('T')[0]; // YYYY-MM-DD format
  }
  // Function to update truck availability
  const updateTruckAvailability = (truckId: string, dateAvailability: {
    [date: string]: {
      available: boolean;
      partnerId?: string;
    };
  }) => {
    setTrucks(prevTrucks => prevTrucks.map(truck => truck.id === truckId ? {
      ...truck,
      availability: {
        ...(truck.availability || {}),
        ...dateAvailability
      }
    } : truck));
  };
  // Function to manage business partner connections
  const handleAddPartner = (partner: Omit<BusinessPartner, 'id' | 'connectedSince' | 'status'>) => {
    const newId = `partner-${Date.now()}`;
    const newPartner: BusinessPartner = {
      ...partner,
      id: newId,
      status: 'pending',
      connectedSince: new Date().toISOString().split('T')[0]
    };
    setBusinessPartners(prev => [...prev, newPartner]);
  };
  const handleUpdatePartnerStatus = (partnerId: string, status: 'active' | 'pending' | 'inactive') => {
    setBusinessPartners(prev => prev.map(partner => partner.id === partnerId ? {
      ...partner,
      status
    } : partner));
  };
  const handleRemovePartner = (partnerId: string) => {
    setBusinessPartners(prev => prev.filter(partner => partner.id !== partnerId));
  };
  // Function to handle viewing job details
  const handleViewJobDetails = (jobId: string) => {
    console.log('View job details for:', jobId);
    alert(`Viewing details for job ${jobId}`);
  };
  // Sample driver data - Updated with mostly valid expiration dates
  const [drivers] = useState<Driver[]>([{
    id: '1',
    name: 'Mike Johnson',
    email: 'mike.johnson@dirtlogics.com',
    phone: '555-123-4567',
    cdlNumber: 'CDL123456789',
    cdlClass: 'Class A',
    cdlExpiration: '2025-06-15',
    medicalCardExpiration: '2025-12-31',
    hireDate: '2020-03-15',
    status: 'active',
    emergencyContact: {
      name: 'Sarah Johnson',
      phone: '555-987-6543',
      relationship: 'Spouse'
    }
  }, {
    id: '2',
    name: 'Tom Rodriguez',
    email: 'tom.rodriguez@dirtlogics.com',
    phone: '555-234-5678',
    cdlNumber: 'CDL987654321',
    cdlClass: 'Class A',
    cdlExpiration: '2025-08-20',
    medicalCardExpiration: '2024-03-15',
    hireDate: '2019-07-10',
    status: 'active',
    emergencyContact: {
      name: 'Maria Rodriguez',
      phone: '555-876-5432',
      relationship: 'Spouse'
    }
  }, {
    id: '3',
    name: 'David Chen',
    email: 'david.chen@dirtlogics.com',
    phone: '555-345-6789',
    cdlNumber: 'CDL456789123',
    cdlClass: 'Class A',
    cdlExpiration: '2025-12-30',
    medicalCardExpiration: '2025-12-30',
    hireDate: '2021-01-20',
    status: 'active',
    emergencyContact: {
      name: 'Lisa Chen',
      phone: '555-765-4321',
      relationship: 'Spouse'
    }
  }, {
    id: '4',
    name: 'Robert Williams',
    email: 'robert.williams@dirtlogics.com',
    phone: '555-456-7890',
    cdlNumber: 'CDL789123456',
    cdlClass: 'Class A',
    cdlExpiration: '2025-12-30',
    medicalCardExpiration: '2025-12-30',
    hireDate: '2018-05-12',
    status: 'active',
    emergencyContact: {
      name: 'Jennifer Williams',
      phone: '555-654-3210',
      relationship: 'Spouse'
    }
  }, {
    id: '5',
    name: 'James Anderson',
    email: 'james.anderson@dirtlogics.com',
    phone: '555-567-8901',
    cdlNumber: 'CDL321654987',
    cdlClass: 'Class A',
    cdlExpiration: '2025-12-30',
    medicalCardExpiration: '2025-12-30',
    hireDate: '2022-02-28',
    status: 'active',
    emergencyContact: {
      name: 'Patricia Anderson',
      phone: '555-543-2109',
      relationship: 'Spouse'
    }
  }, {
    id: '6',
    name: 'Michael Brown',
    email: 'michael.brown@dirtlogics.com',
    phone: '555-678-9012',
    cdlNumber: 'CDL654987321',
    cdlClass: 'Class A',
    cdlExpiration: '2025-12-30',
    medicalCardExpiration: '2025-12-30',
    hireDate: '2019-11-05',
    status: 'active',
    emergencyContact: {
      name: 'Linda Brown',
      phone: '555-432-1098',
      relationship: 'Spouse'
    }
  }, {
    id: '7',
    name: 'Christopher Davis',
    email: 'christopher.davis@dirtlogics.com',
    phone: '555-789-0123',
    cdlNumber: 'CDL987321654',
    cdlClass: 'Class A',
    cdlExpiration: '2025-12-30',
    medicalCardExpiration: '2025-12-30',
    hireDate: '2020-08-17',
    status: 'active',
    emergencyContact: {
      name: 'Michelle Davis',
      phone: '555-321-0987',
      relationship: 'Spouse'
    }
  }, {
    id: '8',
    name: 'Matthew Miller',
    email: 'matthew.miller@dirtlogics.com',
    phone: '555-890-1234',
    cdlNumber: 'CDL147258369',
    cdlClass: 'Class A',
    cdlExpiration: '2025-12-30',
    medicalCardExpiration: '2025-12-30',
    hireDate: '2021-06-03',
    status: 'active',
    emergencyContact: {
      name: 'Amanda Miller',
      phone: '555-210-9876',
      relationship: 'Spouse'
    }
  }, {
    id: '9',
    name: 'Anthony Wilson',
    email: 'anthony.wilson@dirtlogics.com',
    phone: '555-901-2345',
    cdlNumber: 'CDL369258147',
    cdlClass: 'Class A',
    cdlExpiration: '2025-12-30',
    medicalCardExpiration: '2025-12-30',
    hireDate: '2018-12-11',
    status: 'active',
    emergencyContact: {
      name: 'Rebecca Wilson',
      phone: '555-109-8765',
      relationship: 'Spouse'
    }
  }, {
    id: '10',
    name: 'Joshua Moore',
    email: 'joshua.moore@dirtlogics.com',
    phone: '555-012-3456',
    cdlNumber: 'CDL258147369',
    cdlClass: 'Class A',
    cdlExpiration: '2025-12-30',
    medicalCardExpiration: '2025-12-30',
    hireDate: '2019-03-25',
    status: 'active',
    emergencyContact: {
      name: 'Stephanie Moore',
      phone: '555-098-7654',
      relationship: 'Spouse'
    }
  }, {
    id: '11',
    name: 'Andrew Taylor',
    email: 'andrew.taylor@dirtlogics.com',
    phone: '555-123-4567',
    cdlNumber: 'CDL741852963',
    cdlClass: 'Class A',
    cdlExpiration: '2025-12-30',
    medicalCardExpiration: '2025-12-30',
    hireDate: '2020-01-14',
    status: 'active',
    emergencyContact: {
      name: 'Kimberly Taylor',
      phone: '555-987-6543',
      relationship: 'Spouse'
    }
  }, {
    id: '12',
    name: 'Kenneth Jackson',
    email: 'kenneth.jackson@dirtlogics.com',
    phone: '555-234-5678',
    cdlNumber: 'CDL852963741',
    cdlClass: 'Class A',
    cdlExpiration: '2025-12-30',
    medicalCardExpiration: '2025-12-30',
    hireDate: '2021-09-07',
    status: 'active',
    emergencyContact: {
      name: 'Carol Jackson',
      phone: '555-876-5432',
      relationship: 'Spouse'
    }
  }, {
    id: '13',
    name: 'Paul White',
    email: 'paul.white@dirtlogics.com',
    phone: '555-345-6789',
    cdlNumber: 'CDL963741852',
    cdlClass: 'Class A',
    cdlExpiration: '2025-12-30',
    medicalCardExpiration: '2025-12-30',
    hireDate: '2017-04-22',
    status: 'active',
    emergencyContact: {
      name: 'Donna White',
      phone: '555-765-4321',
      relationship: 'Spouse'
    }
  }, {
    id: '14',
    name: 'Mark Harris',
    email: 'mark.harris@dirtlogics.com',
    phone: '555-456-7890',
    cdlNumber: 'CDL159357486',
    cdlClass: 'Class A',
    cdlExpiration: '2025-12-30',
    medicalCardExpiration: '2025-12-30',
    hireDate: '2018-07-16',
    status: 'active',
    emergencyContact: {
      name: 'Nancy Harris',
      phone: '555-654-3210',
      relationship: 'Spouse'
    }
  }, {
    id: '15',
    name: 'Donald Martin',
    email: 'donald.martin@dirtlogics.com',
    phone: '555-567-8901',
    cdlNumber: 'CDL357486159',
    cdlClass: 'Class A',
    cdlExpiration: '2025-12-30',
    medicalCardExpiration: '2025-12-30',
    hireDate: '2020-10-29',
    status: 'active',
    emergencyContact: {
      name: 'Helen Martin',
      phone: '555-543-2109',
      relationship: 'Spouse'
    }
  }, {
    id: '16',
    name: 'Steven Thompson',
    email: 'steven.thompson@dirtlogics.com',
    phone: '555-678-9012',
    cdlNumber: 'CDL486159357',
    cdlClass: 'Class A',
    cdlExpiration: '2025-12-30',
    medicalCardExpiration: '2025-12-30',
    hireDate: '2022-12-08',
    status: 'active',
    emergencyContact: {
      name: 'Dorothy Thompson',
      phone: '555-432-1098',
      relationship: 'Spouse'
    }
  }, {
    id: '17',
    name: 'Edward Garcia',
    email: 'edward.garcia@dirtlogics.com',
    phone: '555-789-0123',
    cdlNumber: 'CDL624813579',
    cdlClass: 'Class A',
    cdlExpiration: '2025-12-30',
    medicalCardExpiration: '2025-12-30',
    hireDate: '2019-02-18',
    status: 'active',
    emergencyContact: {
      name: 'Ruth Garcia',
      phone: '555-321-0987',
      relationship: 'Spouse'
    }
  }, {
    id: '18',
    name: 'Brian Martinez',
    email: 'brian.martinez@dirtlogics.com',
    phone: '555-890-1234',
    cdlNumber: 'CDL813579624',
    cdlClass: 'Class A',
    cdlExpiration: '2025-12-30',
    medicalCardExpiration: '2025-12-30',
    hireDate: '2021-05-13',
    status: 'active',
    emergencyContact: {
      name: 'Sharon Martinez',
      phone: '555-210-9876',
      relationship: 'Spouse'
    }
  }, {
    id: 'dirt-logics-driver',
    name: 'Dirt Logics Driver',
    email: 'driver@dirtlogics.com',
    phone: '555-789-4561',
    cdlNumber: 'CDL987654123',
    cdlClass: 'Class A',
    cdlExpiration: '2026-01-15',
    medicalCardExpiration: '2026-01-15',
    hireDate: '2023-06-01',
    status: 'active',
    emergencyContact: {
      name: 'John Driver',
      phone: '555-123-7890',
      relationship: 'Brother'
    }
  }]);
  // Sample pending jobs from brokers/contractors
  const [pendingJobs] = useState<Job[]>([{
    id: 'job-1',
    jobName: 'Downtown Commercial Complex Phase 1',
    jobNumber: 'DCC-2024-001',
    date: '2024-01-16',
    startTime: '07:00',
    material: 'Gravel',
    truckType: 'dump truck 12',
    loadLocation: 'West Valley Materials Yard - 3500 W 4500 S, West Valley City, UT 84119',
    dumpLocation: 'Downtown SLC Project - 123 Main St, Salt Lake City, UT 84101',
    foreman: 'John Smith',
    foremanPhone: '555-123-4567',
    estimatedHours: 8,
    hourlyRate: 95.0,
    source: 'broker',
    sourceName: 'Alpine Hauling',
    priority: 'high',
    status: 'pending',
    comments: 'Priority job - materials needed early morning'
  }, {
    id: 'job-2',
    jobName: 'Highway 89 Expansion Project',
    jobNumber: 'HWY-2024-002',
    date: '2024-01-17',
    startTime: '08:00',
    material: 'Sand',
    truckType: 'strong arm 14',
    loadLocation: 'Sand Pit North - 1200 N Beck St, Salt Lake City, UT 84116',
    dumpLocation: 'Highway 89 Site - Mile Marker 15, Highway 89, UT',
    foreman: 'Mike Johnson',
    foremanPhone: '555-234-5678',
    estimatedHours: 6,
    hourlyRate: 100.0,
    source: 'contractor',
    sourceName: 'ABC Construction',
    priority: 'medium',
    status: 'pending',
    comments: 'Weather dependent - check conditions'
  }]);
  // Calculate dashboard stats
  const availableTrucks = trucks.filter(t => t.status === 'available').length;
  const trucksInMaintenance = trucks.filter(t => t.status === 'maintenance').length;
  const expiredDocuments = drivers.filter(d => {
    const cdlExpired = new Date(d.cdlExpiration) < new Date();
    const medicalExpired = new Date(d.medicalCardExpiration) < new Date();
    return cdlExpired || medicalExpired;
  }).length;
  const expiringDocuments = drivers.filter(d => {
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
    const cdlExpiring = new Date(d.cdlExpiration) <= thirtyDaysFromNow && new Date(d.cdlExpiration) > new Date();
    const medicalExpiring = new Date(d.medicalCardExpiration) <= thirtyDaysFromNow && new Date(d.medicalCardExpiration) > new Date();
    return cdlExpiring || medicalExpiring;
  }).length;
  // Calculate tomorrow's available trucks
  const tomorrowDate = getDateString(1);
  const tomorrowAvailableTrucks = trucks.filter(truck => truck.availability?.[tomorrowDate] === true).length;
  // Count active broker and contractor connections
  const activeBrokers = businessPartners.filter(partner => partner.role === 'broker' && partner.status === 'active').length;
  const activeContractors = businessPartners.filter(partner => partner.role === 'contractor' && partner.status === 'active').length;
  const tabs = [{
    id: 'overview',
    label: 'Overview',
    icon: <FileTextIcon size={18} data-id="element-4086" />
  }, {
    id: 'fleet',
    label: 'Fleet',
    icon: <TruckIcon size={18} data-id="element-4087" />
  }, {
    id: 'drivers',
    label: 'Drivers',
    icon: <UserIcon size={18} data-id="element-4088" />,
    badge: expiredDocuments > 0 ? expiredDocuments : undefined
  }, {
    id: 'maintenance',
    label: 'Maintenance',
    icon: <WrenchIcon size={18} data-id="element-4089" />,
    badge: trucksInMaintenance > 0 ? trucksInMaintenance : undefined
  }, {
    id: 'availability',
    label: 'Availability',
    icon: <CalendarDaysIcon size={18} data-id="element-4090" />
  }, {
    id: 'jobs',
    label: 'Jobs',
    icon: <ClipboardListIcon size={18} data-id="element-4091" />,
    badge: pendingJobs.length > 0 ? pendingJobs.length : undefined
  }, {
    id: 'profile',
    label: 'Profile',
    icon: <Settings2Icon size={18} data-id="element-4092" />
  }];
  return <div className="p-4 lg:p-6 pt-16 lg:pt-6 w-full" data-id="element-4093">
      <div className="flex justify-between items-center mb-4" data-id="element-4094">
        <div className="w-1/3" data-id="element-4095"></div>
        <h1 className="text-xl lg:text-2xl font-bold text-center w-1/3" data-id="element-4096">
          Trucking Company Dashboard
        </h1>
        <div className="flex justify-end w-1/3" data-id="element-4097">
          {onNavigate && <button className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md flex items-center gap-2" onClick={() => onNavigate('dashboard')} data-id="element-4098">
              Back to Dashboard
            </button>}
        </div>
      </div>

      {/* Overview Stats */}
      {activeTab === 'overview' && <>
          {/* Quick Stats Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 lg:gap-4 mb-4 lg:mb-6" data-id="element-4099">
            <div className="bg-white p-3 lg:p-4 rounded-lg shadow" data-id="element-4100">
              <div className="flex items-center justify-between" data-id="element-4101">
                <div data-id="element-4102">
                  <p className="text-xs lg:text-sm font-medium text-gray-600" data-id="element-4103">
                    Available
                  </p>
                  <p className="text-lg lg:text-2xl font-bold text-green-600" data-id="element-4104">
                    {availableTrucks}
                  </p>
                </div>
                <TruckIcon size={24} className="text-green-500 lg:w-8 lg:h-8" data-id="element-4105" />
              </div>
            </div>
            <div className="bg-white p-3 lg:p-4 rounded-lg shadow" data-id="element-4106">
              <div className="flex items-center justify-between" data-id="element-4107">
                <div data-id="element-4108">
                  <p className="text-xs lg:text-sm font-medium text-gray-600" data-id="element-4109">
                    Available Tomorrow
                  </p>
                  <p className="text-lg lg:text-2xl font-bold text-blue-600" data-id="element-4110">
                    {tomorrowAvailableTrucks}
                  </p>
                </div>
                <CalendarDaysIcon size={24} className="text-blue-500 lg:w-8 lg:h-8" data-id="element-4111" />
              </div>
            </div>
            <div className="bg-white p-3 lg:p-4 rounded-lg shadow" data-id="element-4112">
              <div className="flex items-center justify-between" data-id="element-4113">
                <div data-id="element-4114">
                  <p className="text-xs lg:text-sm font-medium text-gray-600" data-id="element-4115">
                    Pending Jobs
                  </p>
                  <p className="text-lg lg:text-2xl font-bold text-blue-600" data-id="element-4116">
                    {pendingJobs.length}
                  </p>
                </div>
                <ClipboardListIcon size={24} className="text-blue-500 lg:w-8 lg:h-8" data-id="element-4117" />
              </div>
            </div>
            <div className="bg-white p-3 lg:p-4 rounded-lg shadow" data-id="element-4118">
              <div className="flex items-center justify-between" data-id="element-4119">
                <div data-id="element-4120">
                  <p className="text-xs lg:text-sm font-medium text-gray-600" data-id="element-4121">
                    Maintenance
                  </p>
                  <p className="text-lg lg:text-2xl font-bold text-orange-600" data-id="element-4122">
                    {trucksInMaintenance}
                  </p>
                </div>
                <WrenchIcon size={24} className="text-orange-500 lg:w-8 lg:h-8" data-id="element-4123" />
              </div>
            </div>
            <div className="bg-white p-3 lg:p-4 rounded-lg shadow" data-id="element-4124">
              <div className="flex items-center justify-between" data-id="element-4125">
                <div data-id="element-4126">
                  <p className="text-xs lg:text-sm font-medium text-gray-600" data-id="element-4127">
                    Brokers
                  </p>
                  <p className="text-lg lg:text-2xl font-bold text-purple-600" data-id="element-4128">
                    {activeBrokers}
                  </p>
                </div>
                <BuildingIcon size={24} className="text-purple-500 lg:w-8 lg:h-8" data-id="element-4129" />
              </div>
            </div>
            <div className="bg-white p-3 lg:p-4 rounded-lg shadow" data-id="element-4130">
              <div className="flex items-center justify-between" data-id="element-4131">
                <div data-id="element-4132">
                  <p className="text-xs lg:text-sm font-medium text-gray-600" data-id="element-4133">
                    Alerts
                  </p>
                  <p className="text-lg lg:text-2xl font-bold text-red-600" data-id="element-4134">
                    {expiredDocuments + expiringDocuments}
                  </p>
                </div>
                <AlertTriangleIcon size={24} className="text-red-500 lg:w-8 lg:h-8" data-id="element-4135" />
              </div>
            </div>
          </div>

          {/* Action Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 lg:gap-4 mb-4 lg:mb-6" data-id="element-4136">
            <ActionCard icon={<TruckIcon size={20} data-id="element-4138" />} title="Add Truck" onClick={() => setActiveTab('fleet')} color="bg-blue-500 hover:bg-blue-600" data-id="element-4137" />
            <ActionCard icon={<UserIcon size={20} data-id="element-4140" />} title="Add Driver" onClick={() => setActiveTab('drivers')} color="bg-green-500 hover:bg-green-600" data-id="element-4139" />
            <ActionCard icon={<CalendarDaysIcon size={20} data-id="element-4142" />} title="Set Availability" onClick={() => setActiveTab('availability')} color="bg-indigo-500 hover:bg-indigo-600" data-id="element-4141" />
            <ActionCard icon={<WrenchIcon size={20} data-id="element-4144" />} title="Maintenance" onClick={() => setActiveTab('maintenance')} color="bg-orange-500 hover:bg-orange-600" data-id="element-4143" />
            <ActionCard icon={<ClipboardListIcon size={20} data-id="element-4146" />} title="Assign Jobs" onClick={() => setActiveTab('jobs')} color="bg-purple-500 hover:bg-purple-600" data-id="element-4145" />
            <ActionCard icon={<BuildingIcon size={20} data-id="element-4148" />} title="Manage Connections" onClick={() => setActiveTab('profile')} color="bg-gray-500 hover:bg-gray-600" data-id="element-4147" />
          </div>

          {/* In Progress Jobs Section */}
          <div className="bg-white rounded-lg shadow p-4 lg:p-6 mb-4 lg:mb-6" data-id="element-4149">
            <div className="flex items-center justify-between mb-4" data-id="element-4150">
              <h3 className="text-base lg:text-lg font-semibold flex items-center gap-2" data-id="element-4151">
                <TruckIcon size={18} className="text-blue-500" data-id="element-4152" />
                In Progress Jobs
              </h3>
              <span className="text-sm text-gray-500" data-id="element-4153">
                {inProgressJobs.length} active jobs
              </span>
            </div>
            <div className="overflow-x-auto" data-id="element-4154">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-4155">
                <thead className="bg-gray-50" data-id="element-4156">
                  <tr data-id="element-4157">
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4158">
                      Job
                    </th>
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4159">
                      Source
                    </th>
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4160">
                      Truck/Driver
                    </th>
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4161">
                      Material
                    </th>
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4162">
                      Location
                    </th>
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4163">
                      Progress
                    </th>
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4164">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-4165">
                  {inProgressJobs.map(job => <tr key={job.id} className="hover:bg-gray-50" data-id="element-4166">
                      <td className="px-3 py-3 whitespace-nowrap" data-id="element-4167">
                        <div className="font-medium text-gray-900" data-id="element-4168">
                          {job.jobName}
                        </div>
                        <div className="text-sm text-gray-500" data-id="element-4169">
                          {job.jobNumber}
                        </div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap" data-id="element-4170">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${job.source === 'broker' ? 'bg-indigo-100 text-indigo-800' : 'bg-green-100 text-green-800'}`} data-id="element-4171">
                          {job.source === 'broker' ? 'Broker' : 'Contractor'}
                        </span>
                        <div className="text-sm text-gray-500 mt-1" data-id="element-4172">
                          {job.sourceName}
                        </div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap" data-id="element-4173">
                        <div className="text-sm font-medium" data-id="element-4174">
                          {job.assignedTruck}
                        </div>
                        <div className="text-sm text-gray-500" data-id="element-4175">
                          {job.assignedDriver}
                        </div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap" data-id="element-4176">
                        <div className="text-sm" data-id="element-4177">{job.material}</div>
                        <div className="text-xs text-gray-500" data-id="element-4178">
                          {job.truckType}
                        </div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap" data-id="element-4179">
                        <div className="text-sm flex items-center gap-1" data-id="element-4180">
                          <MapPinIcon size={14} className="text-gray-400" data-id="element-4181" />
                          {job.loadLocation}
                        </div>
                        <div className="text-sm flex items-center gap-1" data-id="element-4182">
                          <ArrowRightIcon size={14} className="text-gray-400" data-id="element-4183" />
                          {job.dumpLocation}
                        </div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap" data-id="element-4184">
                        <div className="flex items-center gap-2" data-id="element-4185">
                          <div className="w-24 bg-gray-200 rounded-full h-2" data-id="element-4186">
                            <div className={`h-2 rounded-full ${job.status === 'delayed' ? 'bg-orange-500' : 'bg-blue-600'}`} style={{
                        width: `${job.progress}%`
                      }} data-id="element-4187"></div>
                          </div>
                          <span className="text-sm font-medium" data-id="element-4188">
                            {job.loadCount}/{job.totalLoads} loads
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 mt-1" data-id="element-4189">
                          {job.tonnage && `${job.tonnage.current}/${job.tonnage.total} tons (${job.progress}%)`}
                          {job.status === 'delayed' && <span className="ml-2 text-orange-600 font-medium" data-id="element-4190">
                              Delayed
                            </span>}
                        </div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-sm" data-id="element-4191">
                        <div className="flex flex-col space-y-1" data-id="element-4192">
                          <button className="text-blue-600 hover:text-blue-800" onClick={() => handleViewJobDetails(job.id)} data-id="element-4193">
                            View Details
                          </button>
                          <JobMessageButton jobId={job.id} jobName={job.jobName} recipientRole="driver" buttonText="Message Driver" className="text-green-600 hover:text-green-800 flex items-center gap-1" data-id="element-4194" />
                          <JobMessageButton jobId={job.id} jobName={job.jobName} recipientRole={job.source} buttonText={`Message ${job.source === 'broker' ? 'Broker' : 'Contractor'}`} className={`${job.source === 'broker' ? 'text-indigo-600 hover:text-indigo-800' : 'text-purple-600 hover:text-purple-800'} flex items-center gap-1`} data-id="element-4195" />
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>

          {/* Alerts Section */}
          {(expiredDocuments > 0 || expiringDocuments > 0 || trucksInMaintenance > 0) && <div className="bg-white rounded-lg shadow p-4 lg:p-6 mb-4 lg:mb-6" data-id="element-4196">
              <h3 className="text-base lg:text-lg font-semibold mb-3 lg:mb-4 flex items-center gap-2" data-id="element-4197">
                <AlertTriangleIcon size={18} className="text-red-500" data-id="element-4198" />
                Important Alerts
              </h3>
              <div className="space-y-3" data-id="element-4199">
                {expiredDocuments > 0 && <div className="bg-red-50 border border-red-200 rounded-md p-3" data-id="element-4200">
                    <div className="flex items-center gap-2" data-id="element-4201">
                      <AlertTriangleIcon size={16} className="text-red-500" data-id="element-4202" />
                      <span className="font-medium text-red-800 text-sm" data-id="element-4203">
                        {expiredDocuments} driver(s) have expired documents
                      </span>
                    </div>
                    <p className="text-xs lg:text-sm text-red-600 mt-1" data-id="element-4204">
                      Drivers with expired CDL or medical cards cannot operate
                      vehicles
                    </p>
                  </div>}
                {expiringDocuments > 0 && <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3" data-id="element-4205">
                    <div className="flex items-center gap-2" data-id="element-4206">
                      <CalendarIcon size={16} className="text-yellow-500" data-id="element-4207" />
                      <span className="font-medium text-yellow-800 text-sm" data-id="element-4208">
                        {expiringDocuments} document(s) expiring within 30 days
                      </span>
                    </div>
                    <p className="text-xs lg:text-sm text-yellow-600 mt-1" data-id="element-4209">
                      Schedule renewals to avoid service interruptions
                    </p>
                  </div>}
                {trucksInMaintenance > 0 && <div className="bg-orange-50 border border-orange-200 rounded-md p-3" data-id="element-4210">
                    <div className="flex items-center gap-2" data-id="element-4211">
                      <WrenchIcon size={16} className="text-orange-500" data-id="element-4212" />
                      <span className="font-medium text-orange-800 text-sm" data-id="element-4213">
                        {trucksInMaintenance} truck(s) currently in maintenance
                      </span>
                    </div>
                    <p className="text-xs lg:text-sm text-orange-600 mt-1" data-id="element-4214">
                      Check maintenance status and estimated completion dates
                    </p>
                  </div>}
              </div>
            </div>}
        </>}

      {/* Tab Navigation */}
      <TabNavigation tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} data-id="element-4215" />

      {/* Tab Content */}
      <div data-id="element-4216">
        {activeTab === 'fleet' && <FleetManagement trucks={trucks} drivers={drivers} data-id="element-4217" />}
        {activeTab === 'drivers' && <DriverManagement drivers={drivers} data-id="element-4218" />}
        {activeTab === 'maintenance' && <MaintenanceSchedule trucks={trucks} data-id="element-4219" />}
        {activeTab === 'availability' && <TruckAvailability trucks={trucks} updateTruckAvailability={updateTruckAvailability} businessPartners={businessPartners} data-id="element-4220" />}
        {activeTab === 'jobs' && <JobAssignments jobs={pendingJobs} trucks={trucks} drivers={drivers} data-id="element-4221" />}
        {activeTab === 'profile' && <TruckingCompanyProfile businessPartners={businessPartners} onAddPartner={handleAddPartner} onUpdatePartnerStatus={handleUpdatePartnerStatus} onRemovePartner={handleRemovePartner} data-id="element-4222" />}
      </div>
    </div>;
};